import { type Passage, trimPassage } from './review-transcript';

/**
 * Reading the browser's selection as a position in a message (relecture.md
 * §5.2, §16) - the only DOM-bound module of the review, kept thin so the rest
 * is testable without one.
 *
 * ## What carries a message
 *
 * An element with `data-review-text` (`reply` or `question`) and `data-turn` (the
 * 1-based number). Its `textContent` IS the message, character for character:
 * the pastilles of the marks are `::before` content and every accessible name
 * is an attribute, so the length of the text before a selection is its position
 * in the message, in UTF-16 units like the contract's (`review-transcript.ts`).
 *
 * ## One message, or nothing
 *
 * A selection that starts in one message and ends in another is refused in one
 * sentence (`SELECTION_REFUSAL`). A selection that merely RUNS OVER the edge of
 * its message onto nothing but blank space - a triple click, which ends at the
 * start of the next block - is the message, clamped. One that holds anything
 * else past the edge is two messages.
 */

export type ReviewSelectionKind = 'reply' | 'question';

/** What a selection inside ONE message says: where it is, and where to point at it. */
interface Located {
  readonly turn: number;
  readonly passage: Passage;
  /** The rectangle of the selection NOW (it moves with the page), or null where the DOM has none. */
  readonly rect: () => DOMRect | null;
}

export type ReplySelection = Located & { readonly kind: 'reply' };
export type QuestionSelection = Located & { readonly kind: 'question' };

export type SelectionRead =
  | ReplySelection
  | QuestionSelection
  | {
      readonly kind: 'refused';
      /** Where to say it: the rectangle of the selection NOW, or null where the DOM has none. */
      readonly rect: () => DOMRect | null;
    };

/** The element that carries the message a node is in, or null. */
function holderOf(node: Node): HTMLElement | null {
  const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element);
  const holder = element?.closest<HTMLElement>('[data-review-text]');
  return holder ?? null;
}

/** Is everything the range holds blank? */
function isBlank(range: Range): boolean {
  return range.toString().trim() === '';
}

/**
 * The part of `range` inside `host`, or null when it reaches past `host` over
 * something that is not blank.
 */
function clampTo(range: Range, host: HTMLElement): Range | null {
  const clamped = range.cloneRange();
  if (!host.contains(range.startContainer)) {
    const head = document.createRange();
    head.setStart(range.startContainer, range.startOffset);
    head.setEnd(host, 0);
    if (!isBlank(head)) return null;
    clamped.setStart(host, 0);
  }
  if (!host.contains(range.endContainer)) {
    const tail = document.createRange();
    tail.setStart(host, host.childNodes.length);
    tail.setEnd(range.endContainer, range.endOffset);
    if (!isBlank(tail)) return null;
    clamped.setEnd(host, host.childNodes.length);
  }
  return clamped;
}

/**
 * What the selection is, inside `root`: a passage of a reply (to range), a
 * passage of an interviewer's message (to annotate), a refusal, or null when
 * there is nothing to act on - nothing selected, only spaces, or a selection
 * that is not in the transcript at all.
 */
export function readReviewSelection(
  root: HTMLElement | null,
  selection: Selection | null,
): SelectionRead | null {
  if (root === null || selection === null || selection.rangeCount === 0 || selection.isCollapsed) {
    return null;
  }
  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;
  const rectOf = (of: Range) => (): DOMRect | null =>
    typeof of.getBoundingClientRect === 'function' ? of.getBoundingClientRect() : null;
  const refused: SelectionRead = { kind: 'refused', rect: rectOf(range.cloneRange()) };

  const first = holderOf(range.startContainer);
  const last = holderOf(range.endContainer);
  if (first === null && last === null) return null;
  if (first !== null && last !== null && first !== last) return refused;

  const host = first ?? last;
  if (host === null) return null;
  const inside = clampTo(range, host);
  if (inside === null) return refused;

  const kind = host.dataset.reviewText;
  const turn = Number(host.dataset.turn);
  if ((kind !== 'reply' && kind !== 'question') || !Number.isInteger(turn)) return null;

  const before = document.createRange();
  before.selectNodeContents(host);
  before.setEnd(inside.startContainer, inside.startOffset);
  const start = before.toString().length;
  const raw = inside.toString();
  const passage = trimPassage(host.textContent ?? '', start, start + raw.length);
  if (passage === null) return null;

  return { kind, turn, passage, rect: rectOf(inside.cloneRange()) };
}
